import {
  platformStoreSet,
  platformStoreDelete,
  platformStoreDeleteAll,
  platformStoreGet,
  triggerSentryError,
} from '../../../shared/helpers.js'
import configuration from '../configuration.js'

type SetParamsType = {
  url: string
  method: string
  data: any
  rejected: boolean
}

class NetworkCache {
  defaultTTl: number
  exemptedUrls: string[]

  constructor() {
    this.defaultTTl = 120 // 2 minutes in seconds

    this.exemptedUrls = [
      'https://profile.services.pbs.org/v1/', // activation
      'https://content.services.pbs.org/v3/samsungtv/profile/', // user profile, needed for change-station
      'https://content.services.pbs.org/v3/viziotv/profile/',
      'https://tvault-staging.services.pbs.org', //@TODO: This URL will change to the production URL
      configuration.featureTogglesBaseURL.dev,
      configuration.featureTogglesBaseURL.prod,
    ]
  }

  check(url: string, method: string) {
    if (!this.isSupportedMethod(method)) return null

    const prefix = this.prefix(url)

    const cacheHit = platformStoreGet(prefix)

    if (!cacheHit) {
      // cache miss, return null
      return null
    }

    try {
      const cacheData = JSON.parse(cacheHit)

      if (this.currentTimestamp() - cacheData.timestamp > this.defaultTTl) {
        // Cache hit, but expired
        this.expire(prefix)
        return null
      }

      // Valid Cache Hit
      return cacheData
    } catch (error) {
      triggerSentryError('Could not parse JSON', 'networkCache', {
        errorMessage: error.message ?? 'Undefined error message (default)',
      })
      return null
    }
  }

  isSupportedMethod(method: string) {
    return method === 'GET'
  }

  isExempted(url: string) {
    return this.exemptedUrls.some((exemptedUrl) => url.startsWith(exemptedUrl))
  }

  clearCache() {
    platformStoreDeleteAll('netcache:')
  }

  expire(prefix: string) {
    // platformStoreDelete(prefix)
    platformStoreDelete(prefix)
  }

  set({ url, method, data, rejected }: SetParamsType): null | undefined {
    // @ts-ignore
    if (COMPILING_FOR_COMCASTX1) return null
    if (!this.isSupportedMethod(method)) return null
    if (this.isExempted(url)) {
      // prevent the usage of the cache on urls that should never be cached
      return null
    }

    const prefix = this.prefix(url)

    const ts = this.currentTimestamp()

    const cache = JSON.stringify({
      timestamp: ts,
      rejected,
      data,
    })

    try {
      if (!this.storageAvailable(prefix, cache)) {
        this.clearCache()
      }
      platformStoreSet(prefix, cache)
    } catch (e) {
      this.clearCache()
    }
  }

  prefix(url: string) {
    return `netcache:${url}`
  }

  currentTimestamp() {
    const unixEpoch = (new Date().getTime() / 1000).toString()
    return parseInt(unixEpoch)
  }

  storageAvailable(prefix: any, cache: any) {
    try {
      platformStoreSet(prefix, cache)
      platformStoreDelete(prefix)
      return true
    } catch (e) {
      if (this.isQuotaExceededError(e)) {
        return false
      }
      //default error
      return true
    }
  }

  isQuotaExceededError(err: unknown) {
    return (
      err instanceof DOMException &&
      (err.code === 22 || err.name === 'QuotaExceededError')
    )
  }
}

export default NetworkCache
